import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, Typography, Paper, Stepper, Step, StepLabel,
  TextField, Grid, Alert, Autocomplete, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'
import ContactFormDialog from '../../../../components/ui/ContactFormDialog'

const STEPS = ['Übergabedaten', 'Auswirkungen prüfen', 'Bestätigen & Durchführen']

OwnershipChange.layout = (page) => <AppShell>{page}</AppShell>

export default function OwnershipChange({ property, contract, contacts: initialContacts, reasons }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const flash = props.flash || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}/contracts`

  const [activeStep, setActiveStep] = useState(0)
  const [contacts, setContacts] = useState(initialContacts)
  const [formData, setFormData] = useState({
    transfer_date: '',
    new_contact_id: null,
    reason: '',
    notes: '',
  })
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState([])
  const [executing, setExecuting] = useState(false)
  const [contactDialogOpen, setContactDialogOpen] = useState(false)

  // Show flash alert errors from redirect
  const flashErrors = flash.alert ? [flash.alert] : []
  const allErrors = [...errors, ...flashErrors]

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!validateStep1()) return
      await fetchPreview()
    } else if (activeStep === 1) {
      setActiveStep(2)
    }
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
    setErrors([])
  }

  const validateStep1 = () => {
    const errs = []
    if (!formData.transfer_date) errs.push('Übergabedatum ist erforderlich.')
    if (!formData.new_contact_id) {
      errs.push('Neuer Eigentümer muss ausgewählt werden.')
    }
    if (!formData.reason) errs.push('Grund ist erforderlich.')
    setErrors(errs)
    return errs.length === 0
  }

  const fetchPreview = async () => {
    setLoading(true)
    setErrors([])
    try {
      const res = await fetch(`${basePath}/${contract.id}/ownership_change_preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ transfer_date: formData.transfer_date }),
      })
      if (res.ok) {
        const data = await res.json()
        setPreview(data.preview)
        setActiveStep(1)
      } else {
        const data = await res.json()
        setErrors([data.error || 'Vorschau konnte nicht geladen werden.'])
      }
    } catch {
      setErrors(['Netzwerkfehler beim Laden der Vorschau.'])
    } finally {
      setLoading(false)
    }
  }

  const handleExecute = () => {
    setExecuting(true)
    setErrors([])

    router.post(`${basePath}/${contract.id}/ownership_change_execute`, {
      transfer_date: formData.transfer_date,
      new_contact_id: formData.new_contact_id,
      reason: formData.reason,
      notes: formData.notes,
    }, {
      onError: (errs) => {
        setErrors(Object.values(errs).flat())
        setExecuting(false)
      },
      onFinish: () => setExecuting(false),
    })
  }

  const handleContactCreated = (newContact) => {
    setContacts((prev) => [...prev, newContact])
    setFormData((prev) => ({ ...prev, new_contact_id: newContact.id }))
    setContactDialogOpen(false)
  }

  const selectedContact = contacts.find((c) => c.id === formData.new_contact_id)

  return (
    <Box>
      <PageHeader
        title="Eigentümerwechsel"
        breadcrumbs={[
          { label: 'Verträge', href: basePath },
          { label: contract.number || `#${contract.id}`, href: `${basePath}/${contract.id}` },
          { label: 'Eigentümerwechsel' },
        ]}
      />

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {allErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }} data-testid="error-alert">
            {allErrors.map((e, i) => <div key={i}>{e}</div>)}
          </Alert>
        )}

        {activeStep === 0 && (
          <Step1Form
            formData={formData}
            setFormData={setFormData}
            contacts={contacts}
            reasons={reasons}
            onOpenContactDialog={() => setContactDialogOpen(true)}
          />
        )}

        {activeStep === 1 && preview && (
          <Step2Preview
            preview={preview}
            contract={contract}
            formData={formData}
            selectedContact={selectedContact}
          />
        )}

        {activeStep === 2 && (
          <Step3Confirm
            contract={contract}
            formData={formData}
            selectedContact={selectedContact}
            preview={preview}
          />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={activeStep === 0 ? () => router.visit(`${basePath}/${contract.id}`) : handleBack}
            data-testid="cancel-btn"
          >
            {activeStep === 0 ? 'Abbrechen' : 'Zurück'}
          </Button>
          {activeStep < 2 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
              data-testid="next-btn"
            >
              {loading ? <CircularProgress size={20} /> : 'Weiter'}
            </Button>
          )}
          {activeStep === 2 && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleExecute}
              disabled={executing}
              data-testid="execute-btn"
            >
              {executing ? <CircularProgress size={20} /> : 'Durchführen'}
            </Button>
          )}
        </Box>
      </Paper>

      <ContactFormDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        onContactCreated={handleContactCreated}
      />
    </Box>
  )
}

function Step1Form({ formData, setFormData, contacts, reasons, onOpenContactDialog }) {
  return (
    <Grid container spacing={3} sx={{ maxWidth: 600 }}>
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Übergabedatum"
          type="date"
          value={formData.transfer_date}
          onChange={(e) => setFormData({ ...formData, transfer_date: e.target.value })}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
          required
          data-testid="transfer-date-field"
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Autocomplete
          options={contacts}
          getOptionLabel={(c) => c.name}
          value={contacts.find((c) => c.id === formData.new_contact_id) || null}
          onChange={(_, val) => setFormData({ ...formData, new_contact_id: val?.id || null })}
          renderInput={(params) => (
            <TextField {...params} label="Neuer Eigentümer" required data-testid="contact-select" />
          )}
        />
        <Button
          size="small"
          startIcon={<AddIcon />}
          sx={{ mt: 1 }}
          onClick={onOpenContactDialog}
          data-testid="create-contact-btn"
        >
          Neuen Kontakt anlegen
        </Button>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          select
          label="Grund"
          value={formData.reason}
          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
          fullWidth
          required
          data-testid="reason-field"
        >
          {reasons.map((r) => (
            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Notiz"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          fullWidth
          multiline
          rows={3}
          data-testid="notes-field"
        />
      </Grid>
    </Grid>
  )
}

function Step2Preview({ preview, contract, formData, selectedContact }) {
  const transferDate = formatDate(formData.transfer_date)
  const endDate = formatDate(preview.old_contract.end_date)

  return (
    <Box sx={{ maxWidth: 700 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Vertrag (bisheriger Eigentümer)</Typography>
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 500 }}>Vertrag</TableCell>
              <TableCell>{contract.number}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 500 }}>Wird beendet zum</TableCell>
              <TableCell>{endDate}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 500 }}>Aktive Sollstellungen</TableCell>
              <TableCell>
                {preview.old_contract.generators_to_terminate.map((g, i) => (
                  <div key={i}>{g.receivable_type}: <FormattedCurrency value={g.amount} /></div>
                ))}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 2 }}>Vertrag (neuer Eigentümer)</Typography>
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 500 }}>Beginnt am</TableCell>
              <TableCell>{transferDate}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 500 }}>Neue Sollstellungen</TableCell>
              <TableCell>
                {preview.new_contract.generators.map((g, i) => (
                  <div key={i}>{g.receivable_type}: <FormattedCurrency value={g.amount} /> / Monat</div>
                ))}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 2 }}>Hausgeldabrechnung</Typography>
      <Alert severity="info" data-testid="settlement-info">
        Abrechnungsmethode: Pro-rata Aufteilung (Standard)
      </Alert>
    </Box>
  )
}

function Step3Confirm({ contract, formData, selectedContact, preview }) {
  const contactName = selectedContact?.name || '–'
  const reasonLabels = { sale: 'Verkauf', inheritance: 'Erbschaft', gift: 'Schenkung', other: 'Sonstiges' }

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Zusammenfassung Eigentümerwechsel</Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={1}>
          <Grid size={{ xs: 12 }}>
            <SummaryRow label="Einheit" value={contract.unit_number} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <SummaryRow label="Übergabedatum" value={formatDate(formData.transfer_date)} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <SummaryRow label="Grund" value={reasonLabels[formData.reason] || formData.reason} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <SummaryRow label="Bisheriger Eigentümer" value={`${contract.number}`} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <SummaryRow label="Neuer Eigentümer" value={contactName} />
          </Grid>
          {formData.notes && (
            <Grid size={{ xs: 12 }}>
              <SummaryRow label="Notiz" value={formData.notes} />
            </Grid>
          )}
        </Grid>
      </Paper>

      <Alert severity="warning" sx={{ mt: 2 }} data-testid="confirm-warning">
        Der Eigentümerwechsel kann nicht rückgängig gemacht werden. Bitte prüfen Sie die Angaben sorgfältig.
      </Alert>
    </Box>
  )
}

function SummaryRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180, fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  )
}

function formatDate(dateStr) {
  if (!dateStr) return '–'
  const d = new Date(dateStr)
  if (isNaN(d)) return dateStr
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
